import { useEffect, useRef, useState } from "react";
import { X, Fuel, Gauge, Calendar, Car, Palette, Settings } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import { vehiclePrimaryImage } from "@/utils/vehicleImages";
import { formatVehiclePrice } from "@/utils/vehiclePrice";

export default function CompareModal({ vehicles, open, onClose, onRemoveVehicle }) {
  const { t, language } = useTranslation();
  const { isDark } = useTheme();
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const [compactHeader, setCompactHeader] = useState(false);

  useEffect(() => {
    if (!open) {
      setCompactHeader(false);
      return undefined;
    }
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setCompactHeader(!entry.isIntersecting),
      { root, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, vehicles.length]);

  if (!open || vehicles.length === 0) return null;

  const localizedField = (vehicle, enKey, esKey) => {
    if (language === "es") {
      const es = vehicle?.[esKey];
      if (typeof es === "string" && es.trim()) return es;
    }
    return vehicle?.[enKey];
  };

  const vehicleFeatureLabels = (vehicle) =>
    (vehicle.features || [])
      .map((en, idx) => {
        if (language === "es" && Array.isArray(vehicle.features_es) && vehicle.features_es[idx]) {
          return String(vehicle.features_es[idx]).trim();
        }
        return typeof en === "string" ? en.trim() : "";
      })
      .filter(Boolean);

  const specs = [
    { label: t('compare.price'), key: "price", format: (v, vehicle) => formatVehiclePrice(vehicle) || t('inventory.getQuote'), icon: null },
    { label: t('compare.year'), key: "year", format: (v) => v || 'N/A', icon: Calendar },
    { label: t('compare.mileage'), key: "mileage", format: (v) => v ? `${v.toLocaleString()} mi` : 'N/A', icon: Gauge },
    { label: t('compare.fuelType'), key: "fuel_type", format: (v) => v || 'N/A', icon: Fuel },
    { label: t('compare.transmission'), key: "transmission", format: (v) => v || 'N/A', icon: Settings },
    { label: t('compare.drivetrain'), key: "drivetrain", format: (v) => v || 'N/A', icon: Car },
    { label: t('compare.exteriorColor'), key: "exterior_color", esKey: "exterior_color_es", format: (v) => v || 'N/A', icon: Palette },
    { label: t('compare.interiorColor'), key: "interior_color", esKey: "interior_color_es", format: (v) => v || 'N/A', icon: Palette },
    { label: t('compare.engine'), key: "engine", esKey: "engine_es", format: (v) => v || 'N/A', icon: null },
    { label: t('compare.status'), key: "status", format: (v) => v?.charAt(0).toUpperCase() + v?.slice(1) || 'N/A', icon: null },
  ];

  const bgMain = isDark ? 'bg-[#0A0A0A]' : 'bg-white';
  const borderCls = isDark ? 'border-white/10' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-600';
  const textMuted = isDark ? 'text-gray-500' : 'text-gray-500';
  const accentCls = isDark ? 'text-blue-500' : 'text-red-600';
  const stripeBg = isDark ? 'bg-white/[0.02]' : 'bg-gray-50/50';
  const gridCols = { gridTemplateColumns: `200px repeat(${vehicles.length}, minmax(200px, 1fr))` };

  const vehicleTitle = (vehicle) =>
    `${vehicle.year} ${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ''}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm ${isDark ? 'bg-black/90' : 'bg-black/50'}`}
          onClick={onClose}>
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className={`${bgMain} border ${borderCls} w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()} data-testid="compare-modal">
            
            <div className={`flex items-center justify-between p-6 border-b ${borderCls} flex-shrink-0`}>
              <div>
                <h2 className={`font-unbounded text-2xl font-bold ${textPrimary}`}>{t('compare.title')}</h2>
                <p className={`${textSecondary} text-sm mt-1`}>{vehicles.length} {vehicles.length !== 1 ? t('compare.vehiclesSelected') : t('compare.vehicleSelected')}</p>
              </div>
              <button onClick={onClose} className={`${textSecondary} hover:${textPrimary} transition-colors p-2 ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`} data-testid="close-compare-modal">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-auto">
              <div className="min-w-max">
                <div ref={sentinelRef} className="h-px w-full" aria-hidden />

                <div className="grid" style={gridCols}>
                  <div className="p-4" />
                  {vehicles.map((vehicle) => (
                    <div key={vehicle.id} className={`p-4 text-center border-l ${borderCls}`}>
                      <img
                        src={vehiclePrimaryImage(vehicle)}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                        className="w-full h-32 object-cover"
                      />
                      <div className={`font-unbounded text-xl font-bold ${textPrimary} mt-3`}>
                        {formatVehiclePrice(vehicle) || t('inventory.getQuote')}
                      </div>
                    </div>
                  ))}
                </div>

                <div className={`sticky top-0 z-20 border-b ${borderCls} ${bgMain} shadow-[0_8px_16px_-12px_rgba(0,0,0,0.45)]`}>
                  <div className="grid" style={gridCols}>
                    <div className={`px-4 ${compactHeader ? 'py-2' : 'py-3'} ${bgMain}`} />
                    {vehicles.map((vehicle) => (
                      <div
                        key={vehicle.id}
                        className={`relative text-center border-l ${borderCls} ${bgMain} transition-all duration-200 ${
                          compactHeader ? 'px-3 py-2' : 'px-4 py-3'
                        }`}
                      >
                        <button
                          onClick={() => onRemoveVehicle(vehicle.id)}
                          className="absolute top-1.5 right-1.5 p-1 bg-red-500/20 hover:bg-red-500/40 text-red-400 hover:text-red-300 transition-colors"
                          data-testid={`remove-compare-${vehicle.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                        <span className={`${accentCls} uppercase tracking-widest font-medium transition-all duration-200 ${
                          compactHeader ? 'text-[10px]' : 'text-xs'
                        }`}>
                          {vehicle.make}
                        </span>
                        <h3 className={`font-unbounded font-semibold ${textPrimary} transition-all duration-200 ${
                          compactHeader ? 'text-sm mt-0.5 leading-tight' : 'text-lg mt-1'
                        }`}>
                          {vehicleTitle(vehicle)}
                        </h3>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`border-b ${borderCls}`}>
                  <div className="grid" style={gridCols}>
                    <div className={`p-4 border-b ${borderCls} ${isDark ? 'bg-blue-500/10' : 'bg-red-500/10'}`}>
                      <span className={`font-unbounded text-sm font-semibold uppercase tracking-wider ${isDark ? 'text-blue-400' : 'text-red-600'}`}>{t('compare.specifications')}</span>
                    </div>
                    {vehicles.map((v) => (<div key={v.id} className={`p-4 border-l border-b ${borderCls} ${isDark ? 'bg-blue-500/5' : 'bg-red-500/5'}`} />))}
                  </div>
                  
                  {specs.map((spec, index) => (
                    <div key={spec.key} className={`grid ${index % 2 === 0 ? stripeBg : ''}`}
                      style={gridCols}>
                      <div className={`p-4 flex items-center gap-2 ${textSecondary} text-sm font-medium`}>
                        {spec.icon && <spec.icon className="w-4 h-4" />}{spec.label}
                      </div>
                      {vehicles.map((vehicle) => {
                        const raw = spec.esKey
                          ? localizedField(vehicle, spec.key, spec.esKey)
                          : vehicle[spec.key];
                        return (
                        <div key={vehicle.id} className={`p-4 ${textPrimary} text-center border-l ${borderCls} flex items-center justify-center`}>
                          {spec.key === 'price' ? (
                            <span className="font-unbounded font-semibold text-lg">{spec.format(raw, vehicle)}</span>
                          ) : spec.format(raw, vehicle)}
                        </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                <div>
                  <div className="grid" style={gridCols}>
                    <div className={`p-4 border-b ${borderCls} ${isDark ? 'bg-green-500/10' : 'bg-green-500/10'}`}>
                      <span className={`font-unbounded text-sm font-semibold uppercase tracking-wider ${isDark ? 'text-green-400' : 'text-green-700'}`}>{t('compare.features')}</span>
                    </div>
                    {vehicles.map((v) => (<div key={v.id} className={`p-4 border-l border-b ${borderCls} ${isDark ? 'bg-green-500/5' : 'bg-green-500/5'}`} />))}
                  </div>
                  <div className={`grid ${stripeBg}`} style={gridCols}>
                    <div className="p-4" />
                    {vehicles.map((vehicle) => {
                      const features = vehicleFeatureLabels(vehicle);
                      return (
                        <div key={vehicle.id} className={`p-4 border-l ${borderCls} text-left`}>
                          {features.length === 0 ? (
                            <p className={`text-sm ${textMuted}`}>—</p>
                          ) : (
                            <ul className={`list-disc pl-5 space-y-1.5 text-sm leading-relaxed ${textPrimary}`}>
                              {features.map((feature, idx) => (
                                <li key={`${vehicle.id}-${idx}`}>{feature}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className={`flex items-center justify-between p-6 border-t ${borderCls} flex-shrink-0 ${bgMain}`}>
              <p className={`${textMuted} text-sm`}>{t('compare.compareUpTo')}</p>
              <button onClick={onClose}
                className={`px-6 py-2 text-white font-semibold transition-colors ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
                data-testid="done-comparing-btn">
                {t('compare.doneComparing')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
